/**
 * Parse / serialize CSS solid colors with optional alpha for Design + table pickers.
 * Opaque colors stay `#rrggbb` (backward compatible); translucent → `rgba(r, g, b, a)`.
 */

export type ParsedCssColor = {
  /** Always `#rrggbb` lowercase for `<input type="color">`. */
  hex: string;
  /** 0–1 */
  alpha: number;
};

const HEX3 = /^#([0-9a-f]{3})$/i;
const HEX4 = /^#([0-9a-f]{4})$/i;
const HEX6 = /^#([0-9a-f]{6})$/i;
const HEX8 = /^#([0-9a-f]{8})$/i;
const RGB = /^rgba?\(\s*([0-9.]+%?)\s*,\s*([0-9.]+%?)\s*,\s*([0-9.]+%?)\s*(?:,\s*([0-9.]+%?))?\s*\)$/i;

export function clampAlpha(n: number): number {
  if (!Number.isFinite(n)) return 1;
  return Math.max(0, Math.min(1, n));
}

/** Round alpha for storage (avoid float noise); keep enough precision for UI %. */
export function roundAlpha(n: number): number {
  const a = clampAlpha(n);
  return Math.round(a * 1000) / 1000;
}

function expandHex3(h: string): string {
  return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toLowerCase();
}

function byteFromChannel(raw: string): number {
  const v = raw.trim();
  if (v.endsWith('%')) {
    const pct = Number(v.slice(0, -1));
    if (!Number.isFinite(pct)) return 0;
    return Math.max(0, Math.min(255, Math.round((pct / 100) * 255)));
  }
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(255, Math.round(n)));
}

function alphaFromToken(raw: string | undefined): number {
  if (raw == null || raw === '') return 1;
  const v = raw.trim();
  if (v.endsWith('%')) {
    const pct = Number(v.slice(0, -1));
    if (!Number.isFinite(pct)) return 1;
    return clampAlpha(pct / 100);
  }
  return clampAlpha(Number(v));
}

function hexFromRgb(r: number, g: number, b: number): string {
  const to = (n: number) => n.toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

function rgbFromHex6(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace(/^#/, '');
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

/**
 * Best-effort parse of a solid CSS color into hex + alpha.
 * Returns null for gradients, images, empty, or unrecognized values.
 */
export function parseCssColor(raw: string): ParsedCssColor | null {
  const v = raw.trim();
  if (!v) return null;

  let m = v.match(HEX8);
  if (m) {
    const h = m[1].toLowerCase();
    return {
      hex: `#${h.slice(0, 6)}`,
      alpha: roundAlpha(parseInt(h.slice(6, 8), 16) / 255),
    };
  }
  m = v.match(HEX6);
  if (m) return { hex: `#${m[1].toLowerCase()}`, alpha: 1 };

  m = v.match(HEX4);
  if (m) {
    const h = m[1].toLowerCase();
    const hex = expandHex3(h.slice(0, 3));
    const aNibble = parseInt(h[3] + h[3], 16);
    return { hex, alpha: roundAlpha(aNibble / 255) };
  }
  m = v.match(HEX3);
  if (m) return { hex: expandHex3(m[1].toLowerCase()), alpha: 1 };

  if (/^[0-9a-f]{6}$/i.test(v)) return { hex: `#${v.toLowerCase()}`, alpha: 1 };
  if (/^[0-9a-f]{3}$/i.test(v)) return { hex: expandHex3(v.toLowerCase()), alpha: 1 };
  if (/^[0-9a-f]{8}$/i.test(v)) {
    const h = v.toLowerCase();
    return {
      hex: `#${h.slice(0, 6)}`,
      alpha: roundAlpha(parseInt(h.slice(6, 8), 16) / 255),
    };
  }

  const rgb = v.match(RGB);
  if (rgb) {
    return {
      hex: hexFromRgb(byteFromChannel(rgb[1]), byteFromChannel(rgb[2]), byteFromChannel(rgb[3])),
      alpha: roundAlpha(alphaFromToken(rgb[4])),
    };
  }

  return null;
}

/**
 * Serialize hex + alpha. Opaque → `#rrggbb`; translucent → `rgba(r, g, b, a)`.
 */
export function serializeCssColor(hex: string, alpha: number = 1): string {
  const parsed = parseCssColor(hex) || { hex: '#000000', alpha: 1 };
  const a = roundAlpha(alpha);
  if (a >= 0.995) return parsed.hex;
  const { r, g, b } = rgbFromHex6(parsed.hex);
  // Prefer compact decimals (0.5 not 0.500)
  const aStr = Number.isInteger(a * 100) ? String(Math.round(a * 100) / 100) : String(a);
  return `rgba(${r}, ${g}, ${b}, ${aStr})`;
}

/** Hex for native color inputs; falls back when value is empty / unparsable. */
export function cssColorToHex6(raw: string, fallback = '#0f766e'): string {
  return parseCssColor(raw)?.hex || fallback;
}

/** 0–100 for range sliders. */
export function alphaToPercent(alpha: number): number {
  return Math.round(clampAlpha(alpha) * 100);
}

export function percentToAlpha(percent: number): number {
  if (!Number.isFinite(percent)) return 1;
  return clampAlpha(percent / 100);
}
